import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faClock, faMapMarkerAlt, faImage,
  faPlus, faTrash, faMessage, faLink, faSpinner,
  faCheckCircle, faTimesCircle, faClock as faClockIcon,
  faUser, faPhone, faEnvelope, faSearch, faTimes,
  faChevronLeft, faChevronRight, faCopy, faCheck,faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

const API_EVENT = `${API_CONFIG.apiUrl}evenements.php`;
const API_INVITATION = `${API_CONFIG.apiUrl}api_invitation.php`;

// ─── Sous-composants ─────────────────────────────────────────────

const InvitationItem = ({ invitation, onDelete, onCopy }) => {
  const { id, nom, prenom, telephone, status, link } = invitation;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status) => {
    const configs = {
      'en attente': { color: '#f59e0b', icon: faClockIcon, label: 'En attente' },
      'acceptée': { color: '#22c55e', icon: faCheckCircle, label: 'Acceptée' },
      'rejetée': { color: '#ef4444', icon: faTimesCircle, label: 'Rejetée' }
    };
    const config = configs[status] || configs['en attente'];
    return (
      <span className="status-badge" style={{ '--badge-color': config.color }}>
        <FontAwesomeIcon icon={config.icon} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="invitation-item">
      <div className="info">
        <span className="name">{prenom} {nom}</span>
        <span className="phone"><FontAwesomeIcon icon={faPhone} /> {telephone}</span>
        <button className="btn-copy" onClick={handleCopy} title="Copier le lien">
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
          {copied ? ' Copié !' : ' Lien'}
        </button>
        {getStatusBadge(status)}
      </div>
      <div className="actions">
        <button className="btn-sm btn-whatsapp" onClick={() => window.open(`https://api.whatsapp.com/send?phone=${telephone.replace(/\s/g, '').replace(/^\+/, '')}`, '_blank')}>
          <FontAwesomeIcon icon={faMessage} /> WhatsApp
        </button>
        <button className="btn-sm btn-danger" onClick={() => onDelete(id)}>
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <span>Page {currentPage} / {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className="toast-copy">
      <FontAwesomeIcon icon={faCheck} style={{ color: '#22c55e' }} />
      <span>{message}</span>
      <button onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────

const EvenementDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Formulaire d'invitation
  const [showForm, setShowForm] = useState(false);
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Toast de copie
  const [toastMessage, setToastMessage] = useState(null);

  // Chargement de l'événement et des invitations
  const fetchEvent = useCallback(async () => {
    try {
      const res = await fetch(`${API_EVENT}?action=get&id=${id}`);
      const data = await res.json();
      if (data.status === 'success') {
        setEvent(data.data);
      } else {
        throw new Error(data.message || 'Événement introuvable');
      }
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await fetch(`${API_INVITATION}?action=list&id_event=${id}`);
      const data = await res.json();
      if (data.status === 'success') {
        setInvitations(data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchEvent();
      await fetchInvitations();
      setLoading(false);
    };
    load();
  }, [fetchEvent, fetchInvitations]);

  // Filtrage et pagination
  const filteredInvitations = useMemo(() => {
    if (!searchTerm.trim()) return invitations;
    const term = searchTerm.toLowerCase().trim();
    return invitations.filter(inv =>
      inv.nom.toLowerCase().includes(term) ||
      inv.prenom.toLowerCase().includes(term) ||
      inv.telephone.includes(term)
    );
  }, [invitations, searchTerm]);

  const totalPages = Math.ceil(filteredInvitations.length / itemsPerPage);
  const paginatedInvitations = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredInvitations.slice(start, start + itemsPerPage);
  }, [filteredInvitations, currentPage]);

  // Réinitialiser la page quand la recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Gestion des invitations
  const handleAddInvitation = async (e) => {
    e.preventDefault();
    if (!nom.trim() || !prenom.trim() || !telephone.trim()) {
      setFormError('Tous les champs sont obligatoires');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const formData = new FormData();
      formData.append('id_event', id);
      formData.append('nom', nom.trim());
      formData.append('prenom', prenom.trim());
      formData.append('telephone', telephone.trim());
      const res = await fetch(`${API_INVITATION}?action=create`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.status === 'success') {
        setInvitations(prev => [data.data, ...prev]);
        setNom('');
        setPrenom('');
        setTelephone('');
        setShowForm(false);
      } else {
        setFormError(data.message || 'Erreur');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInvitation = async (invId) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;
    try {
      const formData = new FormData();
      formData.append('_method', 'DELETE');
      const res = await fetch(`${API_INVITATION}?action=delete&id=${invId}`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.status === 'success') {
        setInvitations(prev => prev.filter(inv => inv.id !== invId));
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCopyLink = (link) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => {
        setToastMessage('Lien copié dans le presse-papiers !');
      }).catch(() => {
        // Fallback
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setToastMessage('Lien copié !');
      });
    } else {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setToastMessage('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="event-detail-loading">
        <div className="spinner"></div>
        <p>Chargement de l'événement...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-error">
        <p>❌ {error || 'Événement introuvable'}</p>
        <Link to="/evenements" className="btn-primary">Retour</Link>
      </div>
    );
  }

  return (
    <div className="event-detail-wrapper">
      <style>{`
        /* ================================================================
           MODERN PREMIUM DARK GLASSMORPHISM – DÉTAIL ÉVÉNEMENT
           ================================================================ */
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

        :root {
          --bg-dark: #0b1124;
          --card-bg: rgba(255, 255, 255, 0.04);
          --card-border: rgba(255, 255, 255, 0.07);
          --text-main: #e8edf5;
          --text-muted: #8ea4c4;
          --accent: #5b8def;
          --accent-gradient: linear-gradient(135deg, #5b8def, #8b5cf6);
          --accent-glow: rgba(91, 141, 239, 0.25);
          --gold: #fbbf24;
          --gold-gradient: linear-gradient(135deg, #fbbf24, #f59e0b);
          --danger: #ef4444;
          --danger-bg: rgba(239, 68, 68, 0.1);
          --success: #22c55e;
        }

        .event-detail-wrapper {
          max-width: 1400px;
          margin: 50px auto 0;
          padding: 2rem;
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-main);
          background: radial-gradient(ellipse at 20% 30%, rgba(30, 60, 120, 0.25) 0%, transparent 60%),
                      radial-gradient(ellipse at 80% 70%, rgba(20, 40, 90, 0.15) 0%, transparent 60%),
                      var(--bg-dark);
          border-radius: 32px;
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.05);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .event-detail-wrapper::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 30% 40%, rgba(91, 141, 239, 0.04) 0%, transparent 70%);
          animation: bgFloat 25s infinite alternate ease-in-out;
          z-index: 0;
        }
        @keyframes bgFloat {
          0% { transform: translate(0,0) scale(1); }
          100% { transform: translate(5%,5%) scale(1.1); }
        }

        .event-detail-hero, .event-detail-content, .invitation-section {
          position: relative;
          z-index: 1;
        }

        /* HERO */
        .event-detail-hero {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          margin-bottom: 2.5rem;
          height: 420px;
          background: #0f1a30;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 12px 40px rgba(0,0,0,0.4);
        }
        .event-detail-hero img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.6s ease;
        }
        .event-detail-hero:hover img {
          transform: scale(1.03);
        }
        .event-detail-hero .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(11,17,36,0.85) 0%, transparent 60%);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 2.5rem;
        }
        .event-detail-hero .hero-overlay h1 {
          font-size: 3rem;
          font-weight: 800;
          margin: 0 0 0.5rem;
          text-shadow: 0 4px 30px rgba(0,0,0,0.5);
          letter-spacing: -0.02em;
        }
        .event-detail-hero .hero-overlay .meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem 2.5rem;
          font-size: 1rem;
          color: #b0c4e0;
        }
        .event-detail-hero .hero-overlay .meta svg {
          color: var(--gold);
          margin-right: 6px;
        }

        /* DÉTAILS */
        .event-detail-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 2rem;
          margin-bottom: 2.5rem;
        }
        .event-detail-content .description {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 20px;
          padding: 1.8rem;
          font-size: 1rem;
          line-height: 1.8;
          color: #ccd8ec;
        }
        .event-detail-content .description h3 {
          margin-top: 0;
          color: var(--text-main);
          font-weight: 600;
          font-size: 1.3rem;
        }
        .event-detail-content .info-card {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 20px;
          padding: 1.8rem;
        }
        .event-detail-content .info-card h4 {
          margin-top: 0;
          font-weight: 600;
          font-size: 1.1rem;
          color: var(--text-main);
        }
        .event-detail-content .info-card p {
          margin: 0.6rem 0;
          color: #b0c4e0;
        }
        .event-detail-content .info-card p strong {
          color: var(--text-main);
        }

        /* SECTION INVITATIONS */
        .invitation-section {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: 1.8rem;
          margin-top: 1rem;
        }
        .invitation-section .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .invitation-section .header h3 {
          font-weight: 700;
          font-size: 1.4rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }
        .header-actions {
          display: flex;
          gap: 0.8rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .search-invitation {
          display: flex;
          align-items: center;
          background: rgba(0,0,0,0.3);
          border: 1px solid var(--card-border);
          border-radius: 50px;
          padding: 0.3rem 0.3rem 0.3rem 1.2rem;
          transition: all 0.3s;
        }
        .search-invitation:focus-within {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(91, 141, 239, 0.15);
        }
        .search-invitation input {
          background: transparent;
          border: none;
          color: var(--text-main);
          outline: none;
          font-size: 0.9rem;
          padding: 0.4rem 0;
          width: 160px;
          font-family: inherit;
        }
        .search-invitation input::placeholder {
          color: var(--text-muted);
        }
        .search-invitation .clear-search {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0.3rem 0.8rem;
        }
        .search-invitation .clear-search:hover {
          color: #fff;
        }

        /* BOUTONS */
        .btn-primary {
          background: var(--accent-gradient);
          border: none;
          border-radius: 50px;
          padding: 0.6rem 1.8rem;
          color: #fff;
          font-weight: 600;
          font-size: 0.9rem;
          font-family: inherit;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          box-shadow: 0 6px 20px var(--accent-glow);
          transition: all 0.3s ease;
        }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(91, 141, 239, 0.35);
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .btn-sm {
          padding: 4px 14px;
          font-size: 0.8rem;
          border-radius: 30px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-family: inherit;
        }
        .btn-whatsapp {
          background: rgba(37, 211, 102, 0.12);
          color: #25d366;
          border: 1px solid rgba(37, 211, 102, 0.15);
        }
        .btn-whatsapp:hover {
          background: rgba(37, 211, 102, 0.2);
        }
        .btn-danger {
          background: var(--danger-bg);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.15);
        }
        .btn-danger:hover {
          background: rgba(239, 68, 68, 0.2);
        }
        .btn-copy {
          background: transparent;
          border: 1px solid var(--card-border);
          border-radius: 30px;
          padding: 2px 12px;
          color: var(--text-muted);
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-family: inherit;
        }
        .btn-copy:hover {
          border-color: var(--gold);
          color: var(--gold);
        }

        /* LISTE INVITATIONS */
        .invitation-list {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-top: 0.5rem;
          max-height: 420px;
          overflow-y: auto;
          padding-right: 4px;
        }
        .invitation-list::-webkit-scrollbar {
          width: 4px;
        }
        .invitation-list::-webkit-scrollbar-thumb {
          background: rgba(91, 141, 239, 0.25);
          border-radius: 4px;
        }

        .invitation-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          padding: 0.8rem 1.2rem;
          transition: all 0.2s;
        }
        .invitation-item:hover {
          border-color: rgba(91, 141, 239, 0.2);
          background: rgba(255,255,255,0.04);
        }
        .invitation-item .info {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem 1.2rem;
          align-items: center;
        }
        .invitation-item .info .name {
          font-weight: 600;
        }
        .invitation-item .info .phone {
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        .invitation-item .info .phone svg {
          margin-right: 4px;
        }
        .invitation-item .actions {
          display: flex;
          gap: 0.5rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 12px;
          border-radius: 40px;
          font-size: 0.7rem;
          font-weight: 600;
          background: color-mix(in srgb, var(--badge-color) 12%, transparent);
          color: var(--badge-color);
          border: 1px solid color-mix(in srgb, var(--badge-color) 25%, transparent);
        }

        /* FORMULAIRE */
        .form-invitation {
          background: rgba(255,255,255,0.02);
          border-radius: 16px;
          padding: 1.2rem;
          margin-bottom: 1.5rem;
          border: 1px solid var(--card-border);
        }
        .form-invitation .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 0.8rem;
          align-items: end;
        }
        .form-invitation .form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .form-invitation label {
          font-size: 0.8rem;
          color: var(--text-muted);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .form-invitation input {
          padding: 0.6rem 0.9rem;
          border-radius: 12px;
          border: 1px solid var(--card-border);
          background: rgba(0,0,0,0.2);
          color: var(--text-main);
          font-size: 0.9rem;
          outline: none;
          transition: 0.2s;
          font-family: inherit;
        }
        .form-invitation input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(91, 141, 239, 0.1);
        }
        .form-invitation .error-msg {
          color: #f87171;
          font-size: 0.85rem;
          margin-top: 0.5rem;
        }

        /* PAGINATION */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--card-border);
        }
        .pagination button {
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 30px;
          padding: 0.3rem 1rem;
          color: var(--text-main);
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
          font-family: inherit;
        }
        .pagination button:hover:not(:disabled) {
          background: rgba(91, 141, 239, 0.1);
          border-color: var(--accent);
        }
        .pagination button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination span {
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        /* TOAST COPIE */
        .toast-copy {
          position: fixed;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
          background: rgba(10, 17, 36, 0.95);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          padding: 0.8rem 2rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          color: var(--text-main);
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
          animation: slideUp 0.3s ease;
        }
        .toast-copy button {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          margin-left: 0.5rem;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        .empty-state {
          text-align: center;
          padding: 2.5rem 1rem;
          color: var(--text-muted);
        }
        .empty-state svg {
          opacity: 0.2;
          font-size: 3rem;
          margin-bottom: 0.8rem;
        }

        /* LOADING / ERROR */
        .event-detail-loading, .event-detail-error {
          max-width: 600px;
          margin: 80px auto;
          text-align: center;
          padding: 2rem;
          background: var(--card-bg);
          border-radius: 24px;
          border: 1px solid var(--card-border);
          color: var(--text-main);
        }
        .spinner {
          border: 3px solid rgba(91, 141, 239, 0.1);
          border-top: 3px solid var(--accent);
          border-radius: 50%;
          width: 48px;
          height: 48px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* RESPONSIVE */
        @media (max-width: 1024px) {
          .event-detail-content {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .event-detail-hero { height: 280px; }
          .event-detail-hero .hero-overlay h1 { font-size: 2rem; }
          .event-detail-wrapper { padding: 1.2rem; }
          .form-invitation .form-row {
            grid-template-columns: 1fr;
            gap: 0.6rem;
          }
          .invitation-item {
            flex-direction: column;
            align-items: stretch;
          }
          .invitation-item .actions {
            justify-content: flex-end;
          }
          .search-invitation input { width: 120px; }
        }
      `}</style>

      {/* HERO */}
      <div className="event-detail-hero">
        {event.images ? (
          <img src={`${API_CONFIG.apiUrl}${event.images}`} alt={event.titres} />
        ) : (
          <div style={{ fontSize: '4rem', opacity: 0.2 }}>
            <FontAwesomeIcon icon={faImage} />
          </div>
        )}
        <div className="hero-overlay">
          <h1>{event.titres}</h1>
          <div className="meta">
            <span><FontAwesomeIcon icon={faCalendarAlt} /> {new Date(event.dates).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            <span><FontAwesomeIcon icon={faClock} /> {event.heures}</span>
            <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {event.adresse}</span>
          </div>
        </div>
      </div>

      {/* DÉTAILS */}
      <div className="event-detail-content">
        <div className="description">
          <h3>📖 Description</h3>
          <p>{event.description || 'Aucune description disponible.'}</p>
        </div>
        <div className="info-card">
          <h4><FontAwesomeIcon icon={faInfoCircle} /> Informations</h4>
          <p><strong>ID :</strong> {event.id}</p>
          <p><strong>Créé le :</strong> {new Date(event.created).toLocaleDateString('fr-FR')}</p>
          {event.participantsLimit && <p><strong>Participants max :</strong> {event.participantsLimit}</p>}
          {event.prix && <p><strong>Prix :</strong> {event.prix} USD</p>}
        </div>
      </div>

      {/* INVITATIONS */}
      <div className="invitation-section">
        <div className="header">
          <h3><FontAwesomeIcon icon={faUser} /> Invitations ({invitations.length})</h3>
          <div className="header-actions">
            <div className="search-invitation">
              <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
              <FontAwesomeIcon icon={faPlus} /> {showForm ? 'Fermer' : 'Ajouter'}
            </button>
          </div>
        </div>

        {showForm && (
          <form className="form-invitation" onSubmit={handleAddInvitation}>
            <div className="form-row">
              <div className="form-group">
                <label>Nom</label>
                <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Prénom</label>
                <input type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input type="text" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="+241..." required />
              </div>
              <div className="form-group">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                  Ajouter
                </button>
              </div>
            </div>
            {formError && <div className="error-msg">{formError}</div>}
          </form>
        )}

        {filteredInvitations.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faUser} />
            <p>{searchTerm ? 'Aucune invitation ne correspond à votre recherche.' : 'Aucune invitation pour le moment.'}</p>
          </div>
        ) : (
          <>
            <div className="invitation-list">
              {paginatedInvitations.map(inv => (
                <InvitationItem
                  key={inv.id}
                  invitation={inv}
                  onDelete={handleDeleteInvitation}
                  onCopy={handleCopyLink}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* TOAST DE COPIE */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
};

export default EvenementDetail;